/** Rule codes the backend stores in mobile_links.suspended_reason / audit newValues.reason (SUSPICION_TEXT). */
export const SUSPEND_RULE_CODES = [
  'DISTINCT_IPS',
  'DISTINCT_DEVICES',
  'REFUSED_PROBES',
  'DEAD_LINK_ATTEMPTS',
  'SALE_VELOCITY',
  'OUTSIDE_HOURS',
  'MANUAL',
] as const;

/** Translated text of a suspend rule code; an unknown code falls back to a generic translated text, never the raw code. */
export function suspendReasonLabel(t: (key: string) => string, code: string | null | undefined): string {
  if (code && (SUSPEND_RULE_CODES as readonly string[]).includes(code)) return t(`suspend_${code}`);
  return t('suspend_UNKNOWN');
}

/** Audit action codes the mobile-links module writes (backend mobile-links.service / controller @Audit). */
export const ACTIVITY_CODES = [
  'CREATE', 'REVOKE', 'REVOKE_ALL', 'LOCKDOWN', 'LOCKDOWN_LIFTED', 'REACTIVATE', 'SUSPEND',
  'EXCHANGE', 'EXCHANGE_REFUSED', 'SALE_REFUSED', 'DISCOUNT_REFUSED', 'REPEATED_DEAD_LINK',
  'RESUME', 'RESUME_REFUSED', 'SESSION_REPLACED', 'SIGNED_OUT', 'CLAIM', 'CLAIM_REFUSED',
] as const;

/** Codes a refused order discount carries in the audit row (MobileDiscountCode). */
export const DISCOUNT_REFUSAL_CODES = [
  'LINK_DISCOUNT_OFF', 'LINK_DISCOUNT_INVALID', 'LINK_DISCOUNT_PERCENT', 'LINK_DISCOUNT_AMOUNT',
  'LINK_DISCOUNT_NEEDS_APPROVAL', 'LINK_DISCOUNT_PIN_LIMIT',
] as const;

/** "SOME_NEW_CODE" -> "Some new code": an unknown code is made readable, never shown raw. */
export function humanizeCode(code: string | null | undefined): string {
  const words = String(code ?? '').replace(/[_\-.:]+/g, ' ').trim().toLowerCase();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface ActivityLike {
  action: string;
  newValues?: {
    outcome?: string; reason?: string; code?: string; linkName?: string | null; discountType?: string; discountValue?: number | null;
    percent?: number | null; amount?: number | null; subtotal?: number | null; count?: number;
  } | null;
}

type Tx = (key: string, opts?: Record<string, unknown>) => string;

/** Title of an activity row: translated for every known action, humanised for an unknown one. */
export function activityLabel(t: Tx, action: string): string {
  return (ACTIVITY_CODES as readonly string[]).includes(action) ? t(`event_${action}`) : humanizeCode(action);
}

/** Detail sentence parts of an activity row (reason, amounts, repeat count) that the audit row carries. */
export function activityDetails(t: Tx, row: ActivityLike, money: (n: number) => string): string[] {
  const v = row.newValues;
  if (!v) return [];
  const out: string[] = [];
  if (row.action === 'DISCOUNT_REFUSED') {
    if (v.code) out.push((DISCOUNT_REFUSAL_CODES as readonly string[]).includes(v.code) ? t(`disc_${v.code}`) : humanizeCode(v.code));
    const asked = v.discountType === 'PERCENTAGE' && v.discountValue != null ? `${v.discountValue}%` : v.amount != null ? money(Number(v.amount)) : null;
    if (asked && v.subtotal != null) out.push(t('discAsked', { asked, subtotal: money(Number(v.subtotal)) }));
    else if (asked) out.push(t('discAskedOnly', { asked }));
    if (v.count && v.count > 1) out.push(t('discRepeated', { count: v.count }));
  }
  return out;
}
